<template>	
	<button-loading-component
		label="График ресурсов"
		:loading="asyncOperationPending"
		@click="onShowTimelineModalClick"
	/>

	<el-dialog v-model="timelineModalVisible" @closed="onTimelineModalCloseClick" width="1200" height="1200">		
		<template #header>
			<div style="display: flex; align-items: center">
				<div>
					<h3 style="margin: 0">Заказ-наряд <span>{{ order.number }}</span> от {{ formatDate() }}</h3>
				</div>
				<div style="padding-left: .6rem; padding-right: .4rem;">
					<el-link @click="onGoPrevDateClick" type="primary" :underline="false">
						<el-icon size="28"><ArrowLeft /></el-icon>
					</el-link>
					<el-link @click="onGoNextDateClick" type="primary" :underline="false">
						<el-icon size="28"><ArrowRight /></el-icon>
					</el-link>
				</div>
			</div>
		</template>		

		<timeline-component 
			:schedule-date="date"
			:schedule="timelineSchedule"
			:branch="order.executiveEntityBranch!.id"
			:visible="timelineComponentVisible"
			@item-updated="onTimelineItemUpdated"
		/>
	</el-dialog>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { ArrowLeft, ArrowRight } from '@element-plus/icons-vue';
import ButtonLoadingComponent from '@/components/ButtonLoadingComponent.vue';
import TimelineComponent from '../timeline/TimelineComponent.vue';
import EmployeeJobScheduleModel from '@/models/EmployeeJobScheduleModel';
import OrderJobAssigneeModel from '@/models/OrderJobAssigneeModel';
import OrderModel from '@/models/OrderModel';
import OrderJobModel from '@/models/OrderJobModel';
import EmployeeModel from '@/models/EmployeeModel';
import Vehicle from '@/models/Vehicle';
import VehicleModel from '@/models/VehicleModel';
import VehicleManufacturer from '@/models/VehicleManufacturer';
import { date as dateFormatter } from '@/helpers/formatters';
import { watch, WatchStopHandle } from 'vue';

@Options({
	props: {
		scheduleDate: {
			type: Date,
			default: new Date(),
			require: true
		},
		order: {
			type: OrderModel,
			required: true
		},
		loadScheduleAsyncHandler: {
			type: Function,
			required: true
		}
	},
	emits: ['scheduled', 'schedule-updated', 'error'],
	components: {
		TimelineComponent,
		ButtonLoadingComponent,
		ArrowLeft,
		ArrowRight
	}
})
export default class OrderTimelineModalComponent extends Vue {
	/** props */
	order!: OrderModel;
	// eslint-disable-next-line
	loadScheduleAsyncHandler!: (branchId: number, date: Date) => Promise<Array<EmployeeJobScheduleModel>>;
	jobsAssignees!: Array<OrderJobAssigneeModel>;
	scheduleDate!: Date;

	/** view-models */
	date: Date | null = null;
	timelineSchedule: Array<EmployeeJobScheduleModel> | null = null;
	asyncOperationPending = false;
	timelineModalVisible = false;
	timelineComponentVisible = false;

	scheduledDateWather: WatchStopHandle | null = null;

	mounted(): void {
		this.date = this.scheduleDate;

		this.scheduledDateWather = watch(
			() => this.scheduleDate,
			(d) => this.date = d
		);
	}

	unmounted(): void {
		if (this.scheduledDateWather) {
			this.scheduledDateWather();
		}
	}

	onShowTimelineModalClick() {		
		const onDataLoadedCallback = () => {
			this.timelineModalVisible = true;
			this.$nextTick().then(() => this.timelineComponentVisible = true);
		}

		if (this.date) {
			this.fetchSchedule(this.order.executiveEntityBranch!.id, this.date, onDataLoadedCallback);
		}
	}

	onTimelineModalCloseClick() {
		this.timelineModalVisible = false;
		this.timelineComponentVisible = false;
	}

	map(a: OrderJobAssigneeModel,
		j: OrderJobModel,
		o: OrderModel,
		index: number) : EmployeeJobScheduleModel {
		const jobSchedule = new EmployeeJobScheduleModel();
		jobSchedule.orderJobAssigneeId = a.id;			
		jobSchedule.scheduledDate = a.scheduledDate ?? '';
		jobSchedule.scheduledStartTime = a.scheduledStartTime ?? '';
		jobSchedule.scheduledEndTime = a.scheduledEndTime ?? '';
		jobSchedule.scheduledEndDate = a.scheduledEndDate ?? '';
		jobSchedule.allocationPercent = a.allocationPercent ?? '';

		jobSchedule.employee = new EmployeeModel(a.employee);
		jobSchedule.job = new OrderJobModel({
			id: j.id,
			estimationHrs: j.estimationHrs,
			jobOrder: j.jobOrder,
			serviceJob: j.serviceJob
		});

		jobSchedule.order = new OrderModel({
			id: o.id,
			number: o.number,
			scheduledStartDate: o.scheduledStartDate,
			vehicle: o.vehicle
		});
		
		if (!jobSchedule.order.id) {
			jobSchedule.orderJobAssigneeId = 0;				
			jobSchedule.order.id = Number.MAX_SAFE_INTEGER;
			jobSchedule.job.id = index;
			jobSchedule.virtual = true;
		} else {
			if (!jobSchedule.orderJobAssigneeId) {
				jobSchedule.orderJobAssigneeId = 0;				
			}

			if (!jobSchedule.job.id) {
				jobSchedule.job.id = index;
			}
		}

		if (!jobSchedule.order.vehicle) {
			jobSchedule.order.vehicle = new Vehicle();
			jobSchedule.order.vehicle.model = new VehicleModel();
			jobSchedule.order.vehicle.model.manufacturer = new VehicleManufacturer();
			jobSchedule.order.vehicle.model.manufacturer.name = 'Текущий ЗН';
		}

		return jobSchedule;
	}

	onTimelineItemUpdated(item: EmployeeJobScheduleModel) {
		this.$emit('item-updated', item);
	}

	formatDate() {
		return dateFormatter.dateTime(this.date, false, true);
	}

	onGoPrevDateClick() {
		if (this.date) {			
			this.timelineComponentVisible = false;
			this.date.setDate(this.date!.getDate() - 1);
			this.fetchSchedule(
				this.order.executiveEntityBranch!.id, 
				this.date, 
				() => this.$nextTick().then(() => this.timelineComponentVisible = true));
		}
	}

	onGoNextDateClick() {
		if (this.date) {			
			this.timelineComponentVisible = false;
			this.date.setDate(this.date!.getDate() + 1);
			this.fetchSchedule(
				this.order.executiveEntityBranch!.id, 
				this.date, 
				() => this.$nextTick().then(() => this.timelineComponentVisible = true));
		}
	}

	fetchSchedule(executiveEntityBranchId: number, date: Date, onLoaded?: Function) {
		this.asyncOperationPending = true;
		this.loadScheduleAsyncHandler(executiveEntityBranchId, date)
			.then(employeesJobsSchedule => {							
				if (!employeesJobsSchedule?.length){
					employeesJobsSchedule = [];
				}

				if (this.order.jobs?.length) {
					
					this.timelineSchedule = [];
										
					// to show actual jobs on chart use the most recent data from UI
					// as UI may have changes are not saved yet
					this.order.jobs.forEach((j, jobIndex) => {
						j.assignees?.forEach((a, assigneeIndex) => {
							// assignee hasn't been saved yet
							if (a.id == 0) {
								const employeeJobSchedule = this.map(a, j, this.order, jobIndex + assigneeIndex);
								this.timelineSchedule!.push(employeeJobSchedule);							
							}

							// assignee exists in order
							if (a.id) {
								const assigneeInDatabase = employeesJobsSchedule.find(_ => _.orderJobAssigneeId == a.id);
								const isAssigneeInDatabase = !!assigneeInDatabase;

								// if assignee is presented on UI and exists in database simultaneously
								// show its actual state from UI on chart (as his details might be changed on the form but not saved yet)
								if (isAssigneeInDatabase) {
									const employeeJobSchedule = this.map(a, j, this.order, jobIndex + assigneeIndex);
									this.timelineSchedule!.push(employeeJobSchedule);
								} else {
									this.timelineSchedule!.push(assigneeInDatabase!);
								}
							}
						});
					});	
					
					// add other scheduled jobs of all orders are existing on the date and related to current order's assignees
					const remainingJobs = employeesJobsSchedule.filter(_ => _.order?.id != this.order.id);
					if (remainingJobs.length) {
						remainingJobs.forEach((o, jobIndex) => {
							if (!o) {
								return;
							}

							const remainingOrderEmployeeId = o.employee?.id;

							if (this.order.jobs) {
								this.order.jobs.forEach(job => {
									if (job.assignees) {
										job.assignees.forEach(a => {
											if (a?.employee?.id == remainingOrderEmployeeId) {
												const jobAssignee = new OrderJobAssigneeModel();
												jobAssignee.id = o.orderJobAssigneeId;
												jobAssignee.scheduledDate = o.scheduledDate;
												jobAssignee.scheduledStartTime = o.scheduledStartTime;
												jobAssignee.scheduledEndTime = o.scheduledEndTime;
												jobAssignee.scheduledEndDate = o.scheduledEndDate;
												jobAssignee.allocationPercent = o.allocationPercent;
												jobAssignee.employee = new EmployeeModel(o.employee);
												const employeeJobSchedule = this.map(jobAssignee, o.job!, o.order!, jobIndex);
												// this job is related to other order, but it has the same assignee as the current order has
												// to prevent re-scheduling of jobs are not related to current order set it isDisabled
												employeeJobSchedule.isDisabled = true;
												this.timelineSchedule!.push(employeeJobSchedule!);
											}
										});
									}
								});
							}
						});
					}

				} else {
					this.timelineSchedule = employeesJobsSchedule;
				}

				if (onLoaded) {
					onLoaded();
				}
			})
			.catch(e => {
				this.$emit('error');
				console.error(e);
			})
			.finally(() => this.asyncOperationPending = false);
	}
}
</script>